import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';

import { bibShape } from '@bibliocommons/bc-prop-types';
import { ModalHeader, ModalContent, ModalFooter } from 'app/components/shared/Modal';
import SelectionCard from 'app/components/bibs/shared/SelectionCard';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';

import accountShape from 'app/shapes/accountShape';
import { BibBriefContainer } from 'app/components/bibs/shared/BibBrief';
import { checkoutTransactionsShape } from 'app/shapes/borrowing';

import './CheckoutForm.scss';

export class CheckoutForm extends React.PureComponent {
  constructor(props) {
    super(props);

    const { bib, digitalFormats } = props;
    const materialPolicy = bib.getIn(['policy', 'materialPolicy'], Immutable.Map());
    const formatRequired = materialPolicy.get('requiresFormatDuringCheckout', false);
    const defaultFormat = formatRequired && digitalFormats.first();

    this.state = {
      formatSelected: defaultFormat ? defaultFormat.get('name') : null,
      durationSelected: defaultFormat ? defaultFormat.get('durations').first() : null
    };
  }

  getFormatNames() {
    const { intl, digitalFormats } = this.props;
    return digitalFormats.map(digitalFormat => {
      return intl.formatMessage({
        id: `digiformat_${digitalFormat.get('name').toLowerCase()}`
      });
    });
  }

  handleCheckout = () => {
    const { currentAccount, checkoutActions, digitalFormats, bib, handleClose } = this.props;
    const selectedFormat = digitalFormats.find(format => format.get('name') === this.state.formatSelected);

    checkoutActions.digitalCheckout({
      accountId: currentAccount.get('id'),
      metadataId: bib.get('id'),
      title: bib.get('title'),
      format: selectedFormat,
      duration: this.state.durationSelected,
      postSuccess: handleClose
    });
  };

  handleFormatChange = name => {
    const { digitalFormats } = this.props;
    const selectedFormat = digitalFormats.find(format => format.get('name') === name);

    this.setState({
      formatSelected: selectedFormat.get('name'),
      durationSelected: selectedFormat.get('durations').first()
    });
  };

  handleDurationChange = durationSelected => {
    this.setState({ durationSelected });
  };

  renderFormatSelectionCards() {
    const { digitalFormats } = this.props;

    return digitalFormats.map(format => {
      const formatName = format.get('name');

      return (
        <SelectionCard
          selected={formatName === this.state.formatSelected}
          value={formatName}
          label={<FormattedMessage id={`digiformat_${formatName.toLowerCase()}`} />}
          name="format"
          handleClick={this.handleFormatChange}
          key={formatName}
        />
      );
    });
  }

  renderDurationSelectionCards() {
    const { digitalFormats } = this.props;
    const selectedFormat = digitalFormats.find(format => format.get('name') === this.state.formatSelected);

    return selectedFormat
      .get('durations')
      .map(duration => (
        <SelectionCard
          selected={duration === this.state.durationSelected}
          value={duration}
          label={<FormattedMessage id="label_duration_length" values={{ days: duration }} />}
          name="duration"
          handleClick={this.handleDurationChange}
          key={duration}
        />
      ));
  }

  renderAvailableFormats() {
    const { bib } = this.props;
    const materialPolicy = bib.getIn(['policy', 'materialPolicy'], Immutable.Map());
    const showFormat = materialPolicy.get('showFormatDuringCheckout', false);
    const formatRequired = materialPolicy.get('requiresFormatDuringCheckout', false);

    if (showFormat && !formatRequired) {
      return <FormattedMessage id="available_as" values={{ formats: this.getFormatNames().join(', ') }} />;
    }

    return null;
  }

  renderDigitalFormats() {
    const { bib } = this.props;
    const materialPolicy = bib.getIn(['policy', 'materialPolicy'], Immutable.Map());
    const formatRequired = materialPolicy.get('requiresFormatDuringCheckout', false);
    if (formatRequired) {
      return (
        <div>
          <div className="format-selector">
            <FormattedMessage id="select_a_format" />
          </div>
          <div className="checkout-format-selector">{this.renderFormatSelectionCards()}</div>
        </div>
      );
    }
    return null;
  }

  renderDigitalFormatDurations() {
    const { bib } = this.props;
    const materialPolicy = bib.getIn(['policy', 'materialPolicy'], Immutable.Map());
    const requiresDuration = materialPolicy.get('requiresDuration', false);
    if (requiresDuration) {
      return (
        <div className="checkout-duration">
          <div className="duration-selector">
            <FormattedMessage id="select_a_duration" />
          </div>
          <div className="checkout-duration-selector">{this.renderDurationSelectionCards()}</div>
        </div>
      );
    }
    return null;
  }

  render() {
    const { bib, checkoutTransactions } = this.props;
    const isCheckingOut = checkoutTransactions.getIn([bib.get('id'), 'isCheckingOut'], false);

    return (
      <div className="cp-check-out-form">
        <ModalHeader title={<FormattedMessage id="checkout" />} />
        <ModalContent>
          <BibBriefContainer metadataId={bib.get('id')} />
          <div className="check-out-available-format">
            {this.renderAvailableFormats()}
            {this.renderDigitalFormats()}
            {this.renderDigitalFormatDurations()}
          </div>
        </ModalContent>
        <ModalFooter
          primaryActionButtonTag={TransactionalSolidButton}
          primaryActionButtonText={<FormattedMessage id="checkout" />}
          isLoading={isCheckingOut}
          handlePrimaryAction={this.handleCheckout}
        />
      </div>
    );
  }
}

CheckoutForm.propTypes = {
  bib: bibShape.isRequired,
  handleClose: PropTypes.func.isRequired,
  currentAccount: accountShape.isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  digitalFormats: ImmutablePropTypes.list.isRequired,
  checkoutTransactions: checkoutTransactionsShape.isRequired
};

export default injectIntl(CheckoutForm);
